import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import DateRangePicker from "components/datePickers/DateRangePicker";
import { PsychologistDropdown } from "components/lib/PsychologistDropdown";
import { usePsychologistUser } from "hooks";
import { useSelectedPsychologist } from "components/booking/hooks/useSelectedPsychologist";
import { useSlotDialogParam } from "hooks/useSlotDialogParam";
import {
  CanceledSlotsTableRowProps,
  columns,
} from "pages/CancelledSlotsPage/cancelledSlots";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { useUserHasRole } from "../../hooks/userHooks";
import { Table } from "../../components/Table";
import { useCancelledBookings } from "./useCancelledBookings";
import { PageContainer } from "../base";
import { Spinner } from "@mindlercare/ui-web";

export const CancelledSlots = () => {
  const { t } = useTranslation();
  const [, setSlotIdParam] = useSlotDialogParam();

  const isAdmin = useUserHasRole("Admin");
  const selectedPsychologist = useSelectedPsychologist();
  const selectedPsychologistUserId = selectedPsychologist?.user.userId ?? null;
  const { psychologist, isLoading } = usePsychologistUser();
  const psychologistUserId = psychologist?.user?.userId ?? null;

  const [dateRangePickerValue, setDateRangePickerValue] = useState<{
    start: CalendarDate;
    end: CalendarDate;
  } | null>({
    start: today(getLocalTimeZone()),
    end: today(getLocalTimeZone()).add({ weeks: 4 }),
  });

  const { bookings, isLoading: bookingsLoading } = useCancelledBookings({
    psychologistUserId: isAdmin
      ? (selectedPsychologistUserId ?? -1)
      : psychologistUserId,
    dateRangeValue: dateRangePickerValue,
  });

  const openSlotModal = async (selectedRow: CanceledSlotsTableRowProps) => {
    if (!bookings) {
      return;
    }

    const treatingPsychologistUserId = bookings.find(
      (x) => x.slotId === selectedRow.slotId,
    )?.psychologistUserId;

    const isAdminOrTreatingPsychologist =
      isAdmin || treatingPsychologistUserId === psychologistUserId;

    if (!isAdminOrTreatingPsychologist) {
      return;
    }

    if (typeof selectedRow.slotId !== "number") {
      return;
    }

    await setSlotIdParam(selectedRow.slotId);
  };

  return (
    <PageContainer>
      <Typography insensitive variant="h1">
        {t("CANCELED_MEETINGS")}
      </Typography>
      {!bookings || bookingsLoading ? (
        <Spinner />
      ) : (
        <>
          {isAdmin && !isLoading ? (
            <DropdownContainer>
              <PsychologistDropdown />
            </DropdownContainer>
          ) : null}
          <DateRangePicker
            label={"Date range"}
            value={dateRangePickerValue}
            onChange={setDateRangePickerValue}
            granularity="day"
            shouldForceLeadingZeros
          />
          <Table data={bookings} columns={columns} onRowClick={openSlotModal} />
        </>
      )}
    </PageContainer>
  );
};

const DropdownContainer = styled.div`
  display: flex;
  align-items: center;
`;
